import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { alpha } from '@mui/material/styles';
import { Box, Paper, TableCell, TableRow, Typography } from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import type { PaginationState, SortingState, VisibilityState } from '@tanstack/react-table';
import { TableSkeleton } from '../../common/loading';
import TanStackDataTable from '../../table/TanStackDataTable';
import TableCsvExportButton from '../../table/TableCsvExportButton';
import { utilizationPrimaryHeaderCellSx } from '../../table/DataTable';
import { CheckboxColumnFilterMenu, DateRangeColumnFilterMenu } from '../../dri-insights/ColumnFilterMenu';
import { SEARCE_COLORS } from '../../../theme';
import { TABLE_STYLES } from '../../../constants/tableStyles';
import type { SkillBadgeRecord } from '../../../types/driInsights';
import {
  buildDriInsightsSkillBadgesDataTableColumns,
  compareDriInsightsSkillBadgeRows,
  DR_INSIGHTS_SKILL_BADGES_COLUMN_MENU_LABELS,
  DR_INSIGHTS_SKILL_BADGES_SORTABLE_IDS,
} from './driInsightsSkillBadgesDataTableColumns';
import { exportCsvWithVisibleColumns } from '../../../utils/csvExport';

export type SkillBadgesSortColumn =
  | 'employee_name'
  | 'quest_name'
  | 'quest_level'
  | 'completion_date'
  | null;

export interface SkillBadgesTableProps {
  isLoading: boolean;
  data: SkillBadgeRecord[];
  formatDate: (dateString: string | null | undefined) => string;
  /** Flat inner chrome when nested in a tabbed card (same as Team Insights embedded tables). */
  embedded?: boolean;
}

type SkillBadgeFilterState = {
  selectedValues?: string[];
  startDate?: Date | null;
  endDate?: Date | null;
};

const SkillBadgesTable: React.FC<SkillBadgesTableProps> = ({ isLoading, data, formatDate, embedded = false }) => {
  const [skillBadgesFilters, setSkillBadgesFilters] = useState<Record<string, SkillBadgeFilterState>>({});
  const [skillBadgesFilterAnchorEl, setSkillBadgesFilterAnchorEl] = useState<Record<string, HTMLElement | null>>({});
  const [skillBadgesTempSelectedValues, setSkillBadgesTempSelectedValues] = useState<Record<string, string[]>>({});
  const [skillBadgesFilterSearchText, setSkillBadgesFilterSearchText] = useState<Record<string, string>>({});
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 10 });
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

  useEffect(() => {
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  }, [skillBadgesFilters]);

  const getSkillBadgesAvailableValues = (columnId: string): string[] => {
    if (!data || data.length === 0) return [];
    const valueSet = new Set<string>();
    const rowsForOptions = data.filter((badge: SkillBadgeRecord) => {
      for (const [filterColId, filter] of Object.entries(skillBadgesFilters)) {
        if (filterColId === columnId) continue;
        if (filter.selectedValues?.length) {
          if (filterColId === 'employee_name' && !filter.selectedValues.includes(badge.employee_name)) return false;
          if (filterColId === 'quest_name' && !filter.selectedValues.includes(badge.quest_name)) return false;
          if (filterColId === 'quest_level' && !filter.selectedValues.includes(badge.quest_level)) return false;
        }
        if (filterColId === 'completion_date' && (filter.startDate || filter.endDate)) {
          if (!badge.completion_date) return false;
          const d = new Date(badge.completion_date);
          if (filter.startDate && d < filter.startDate) return false;
          if (filter.endDate) {
            const end = new Date(filter.endDate);
            end.setHours(23, 59, 59, 999);
            if (d > end) return false;
          }
        }
      }
      return true;
    });
    rowsForOptions.forEach((badge: SkillBadgeRecord) => {
      switch (columnId) {
        case 'employee_name':
          if (badge.employee_name) valueSet.add(badge.employee_name);
          break;
        case 'quest_name':
          if (badge.quest_name) valueSet.add(badge.quest_name);
          break;
        case 'quest_level':
          if (badge.quest_level) valueSet.add(badge.quest_level);
          break;
        case 'completion_date':
          if (badge.completion_date) valueSet.add(badge.completion_date);
          break;
      }
    });
    return Array.from(valueSet).sort();
  };

  const filteredSkillBadges = useMemo(() => {
    return data.filter((badge: SkillBadgeRecord) => {
      if (skillBadgesFilters.employee_name?.selectedValues?.length) {
        if (!skillBadgesFilters.employee_name.selectedValues.includes(badge.employee_name)) return false;
      }
      if (skillBadgesFilters.quest_name?.selectedValues?.length) {
        if (!skillBadgesFilters.quest_name.selectedValues.includes(badge.quest_name)) return false;
      }
      if (skillBadgesFilters.quest_level?.selectedValues?.length) {
        if (!skillBadgesFilters.quest_level.selectedValues.includes(badge.quest_level)) return false;
      }
      if (skillBadgesFilters.completion_date) {
        const { startDate, endDate } = skillBadgesFilters.completion_date;
        if (badge.completion_date) {
          const d = new Date(badge.completion_date);
          if (startDate && d < startDate) return false;
          if (endDate) {
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            if (d > end) return false;
          }
        } else if (startDate || endDate) return false;
      }
      return true;
    });
  }, [data, skillBadgesFilters]);

  const hasActiveFilter = (columnId: string) => {
    const f = skillBadgesFilters[columnId];
    if (!f) return false;
    if (f.selectedValues?.length) return true;
    if (f.startDate || f.endDate) return true;
    return false;
  };

  const handleSkillBadgesFilterClick = (e: React.MouseEvent<HTMLElement>, columnId: string) => {
    setSkillBadgesFilterAnchorEl({ ...skillBadgesFilterAnchorEl, [columnId]: e.currentTarget });
    if (skillBadgesTempSelectedValues[columnId] === undefined) {
      const applied = skillBadgesFilters[columnId]?.selectedValues || [];
      setSkillBadgesTempSelectedValues({ ...skillBadgesTempSelectedValues, [columnId]: applied });
    }
  };

  const handleHeaderSortClick = useCallback((columnId: string) => {
    setSorting((prev) => {
      const cur = prev[0];
      if (cur?.id === columnId) return [{ id: columnId, desc: !cur.desc }];
      return [{ id: columnId, desc: false }];
    });
  }, []);

  const handleSkillBadgesFilterClose = (columnId: string) => {
    setSkillBadgesFilterAnchorEl({ ...skillBadgesFilterAnchorEl, [columnId]: null });
    const t = { ...skillBadgesTempSelectedValues };
    delete t[columnId];
    setSkillBadgesTempSelectedValues(t);
    const s = { ...skillBadgesFilterSearchText };
    delete s[columnId];
    setSkillBadgesFilterSearchText(s);
  };

  const handleSkillBadgesCheckboxToggle = (columnId: string, value: string) => {
    const current = skillBadgesTempSelectedValues[columnId] || [];
    const applied = skillBadgesFilters[columnId]?.selectedValues || [];
    const base = current.length > 0 ? current : applied;
    const next = base.includes(value) ? base.filter((v) => v !== value) : [...base, value];
    setSkillBadgesTempSelectedValues({ ...skillBadgesTempSelectedValues, [columnId]: next });
  };

  const handleSkillBadgesApplyFilter = (columnId: string) => {
    const selected = skillBadgesTempSelectedValues[columnId] || [];
    setSkillBadgesFilters({
      ...skillBadgesFilters,
      [columnId]: { selectedValues: selected.length > 0 ? selected : undefined },
    });
    const t = { ...skillBadgesTempSelectedValues };
    delete t[columnId];
    setSkillBadgesTempSelectedValues(t);
    const s = { ...skillBadgesFilterSearchText };
    delete s[columnId];
    setSkillBadgesFilterSearchText(s);
    handleSkillBadgesFilterClose(columnId);
  };

  const handleSkillBadgesSelectAll = (columnId: string, values: string[]) => {
    setSkillBadgesTempSelectedValues({ ...skillBadgesTempSelectedValues, [columnId]: values });
  };

  const handleSkillBadgesClearAll = (columnId: string) => {
    setSkillBadgesTempSelectedValues({ ...skillBadgesTempSelectedValues, [columnId]: [] });
  };

  const compareRows = useCallback(
    (a: SkillBadgeRecord, b: SkillBadgeRecord, columnId: string) => compareDriInsightsSkillBadgeRows(a, b, columnId),
    []
  );

  const dataTableColumns = useMemo(
    () =>
      buildDriInsightsSkillBadgesDataTableColumns({
        sorting,
        hasActiveFilter,
        onFilterClick: handleSkillBadgesFilterClick,
        onHeaderSortClick: handleHeaderSortClick,
        formatDate,
      }),
    [sorting, formatDate, handleHeaderSortClick, skillBadgesFilters]
  );

  const renderSkillBadgesFilterMenu = (columnId: string, columnLabel: string, availableValues: string[]) => {
    const anchorEl = skillBadgesFilterAnchorEl[columnId];
    const open = Boolean(anchorEl);
    const filter = skillBadgesFilters[columnId] || {};

    if (columnId === 'completion_date') {
      const startDate = filter.startDate ?? null;
      const endDate = filter.endDate ?? null;
      return (
        <DateRangeColumnFilterMenu
          open={open}
          anchorEl={anchorEl}
          onClose={() => handleSkillBadgesFilterClose(columnId)}
          columnLabel={columnLabel}
          startDate={startDate}
          endDate={endDate}
          onDateFilterApply={(s, e) => {
            setSkillBadgesFilters({ ...skillBadgesFilters, [columnId]: { ...filter, startDate: s, endDate: e } });
          }}
          hasFilter={!!(startDate || endDate)}
          onClearFilter={() =>
            setSkillBadgesFilters({ ...skillBadgesFilters, [columnId]: { startDate: null, endDate: null } })
          }
        />
      );
    }

    const hasFilter = !!(filter.selectedValues && filter.selectedValues.length > 0);
    const currentTemp = skillBadgesTempSelectedValues[columnId];
    const currentApplied = filter.selectedValues || [];
    const selectedValues = currentTemp !== undefined ? currentTemp : currentApplied;
    const searchText = skillBadgesFilterSearchText[columnId] || '';
    const filteredValues = availableValues.filter((v) => v.toLowerCase().includes(searchText.toLowerCase()));

    return (
      <CheckboxColumnFilterMenu
        open={open}
        anchorEl={anchorEl}
        onClose={() => handleSkillBadgesFilterClose(columnId)}
        columnLabel={columnLabel}
        options={filteredValues}
        selectedValues={selectedValues}
        onToggle={(v) => handleSkillBadgesCheckboxToggle(columnId, v)}
        searchText={searchText}
        onSearchTextChange={(v) => setSkillBadgesFilterSearchText({ ...skillBadgesFilterSearchText, [columnId]: v })}
        onSelectAll={() => handleSkillBadgesSelectAll(columnId, filteredValues)}
        onClearAll={() => handleSkillBadgesClearAll(columnId)}
        onApply={() => handleSkillBadgesApplyFilter(columnId)}
        hasFilter={hasFilter}
        onClearFilter={() => {
          setSkillBadgesFilters({ ...skillBadgesFilters, [columnId]: { selectedValues: undefined } });
          const t = { ...skillBadgesTempSelectedValues };
          delete t[columnId];
          setSkillBadgesTempSelectedValues(t);
          const s = { ...skillBadgesFilterSearchText };
          delete s[columnId];
          setSkillBadgesFilterSearchText(s);
        }}
      />
    );
  };

  const paginationSx = {
    borderTop: `1px solid ${alpha(SEARCE_COLORS.border, 0.9)}`,
    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
      color: SEARCE_COLORS.textSecondary,
      fontWeight: 500,
    },
    '& .MuiTablePagination-select': { color: SEARCE_COLORS.primary, fontWeight: 600 },
    '& .MuiIconButton-root': {
      color: SEARCE_COLORS.primary,
      '&:hover': { backgroundColor: alpha(SEARCE_COLORS.primary, 0.1) },
      '&.Mui-disabled': { color: SEARCE_COLORS.neutral_300 },
    },
  };

  if (isLoading) {
    return (
      <Paper
        elevation={0}
        sx={{
          borderRadius: 0,
          overflow: 'hidden',
          border: `1px solid ${SEARCE_COLORS.neutral_200}`,
          backgroundColor: SEARCE_COLORS.white,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <TableSkeleton rows={8} columns={4} rowHeight={32} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Loading skill badges...
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <>
      <TanStackDataTable<SkillBadgeRecord>
        data={filteredSkillBadges}
        dataTableColumns={dataTableColumns}
        getRowId={(row, index) => `${row.employee_email}-${row.quest_name}-${index}`}
        sorting={sorting}
        onSortingChange={setSorting}
        pagination={pagination}
        onPaginationChange={setPagination}
        sortableColumnIds={[...DR_INSIGHTS_SKILL_BADGES_SORTABLE_IDS]}
        compareRows={compareRows}
        loading={false}
        empty={false}
        maxHeight={450}
        rowsPerPageOptions={[10, 25, 50, 100]}
        elevation={0}
        headerCellBaseSx={utilizationPrimaryHeaderCellSx}
        embedded={embedded}
        rowHover
        tableSx={{
          width: '100%',
          tableLayout: 'fixed',
          '& .MuiTableCell-root': { borderRight: 'none', borderLeft: 'none' },
          ...(embedded ? { '& thead .MuiTableCell-root': { borderRadius: 0 } } : {}),
        }}
        paperSx={
          embedded
            ? {
                p: 0,
                mb: 0,
                boxShadow: 'none',
                border: 'none',
                borderRadius: 0,
                backgroundColor: 'transparent',
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
              }
            : {
                borderRadius: 0,
                overflow: 'hidden',
                border: `1px solid ${SEARCE_COLORS.neutral_200}`,
                backgroundColor: SEARCE_COLORS.white,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                p: 0,
                mb: 0,
                boxShadow: 'none',
              }
        }
        tableContainerSx={{ flex: 1, border: 'none', borderRadius: 0, outline: 'none' }}
        paginationSx={paginationSx}
        enableColumnVisibility
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={setColumnVisibility}
        columnMenuLabels={DR_INSIGHTS_SKILL_BADGES_COLUMN_MENU_LABELS}
        headerAction={
          <TableCsvExportButton
            disabled={filteredSkillBadges.length === 0}
            onClick={() =>
              exportCsvWithVisibleColumns({
                rows: filteredSkillBadges,
                columnVisibility,
                columnIdsInOrder: DR_INSIGHTS_SKILL_BADGES_SORTABLE_IDS,
                columnLabels: DR_INSIGHTS_SKILL_BADGES_COLUMN_MENU_LABELS,
                getCellText: (row, id) => {
                  const r = row as SkillBadgeRecord;
                  if (id === 'completion_date') return formatDate(r.completion_date);
                  return String((r as unknown as Record<string, unknown>)[id] ?? '');
                },
                filenameBase: 'dri-skill-badges',
              })
            }
          />
        }
        renderEmptyBody={(visibleColumnCount) => (
          <TableRow>
            <TableCell colSpan={visibleColumnCount} sx={{ textAlign: 'center', py: 6, ...TABLE_STYLES.cellEmpty }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <SchoolIcon sx={{ fontSize: 48, mb: 2, color: SEARCE_COLORS.neutral_300 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  No Skill Badges Found
                </Typography>
                <Typography variant="body2">No skill badges found for the selected filters.</Typography>
              </Box>
            </TableCell>
          </TableRow>
        )}
      />
      {renderSkillBadgesFilterMenu(
        'employee_name',
        DR_INSIGHTS_SKILL_BADGES_COLUMN_MENU_LABELS.employee_name,
        getSkillBadgesAvailableValues('employee_name')
      )}
      {renderSkillBadgesFilterMenu(
        'quest_name',
        DR_INSIGHTS_SKILL_BADGES_COLUMN_MENU_LABELS.quest_name,
        getSkillBadgesAvailableValues('quest_name')
      )}
      {renderSkillBadgesFilterMenu(
        'quest_level',
        DR_INSIGHTS_SKILL_BADGES_COLUMN_MENU_LABELS.quest_level,
        getSkillBadgesAvailableValues('quest_level')
      )}
      {renderSkillBadgesFilterMenu(
        'completion_date',
        DR_INSIGHTS_SKILL_BADGES_COLUMN_MENU_LABELS.completion_date,
        getSkillBadgesAvailableValues('completion_date')
      )}
    </>
  );
};

export default SkillBadgesTable;
